import { useCallback, useEffect, useRef, MutableRefObject, RefObject } from "react";
import { ExpandedState, RowSelectionState } from "@tanstack/react-table";
import { useScrollToRow } from "@theorgchartdev/storybook";
import { useTopic } from "@/hooks/useTopic";
import { PlanningTabularState } from "@/providers/PlanningTabularProvider";

interface UseTableHighlightNavigationParams {
  tableRef: RefObject<HTMLDivElement>;
  latestStateRef: RefObject<PlanningTabularState>;
  searchCtrlRef: RefObject<any>;
  getDataCache: () => Record<string, any>;
  updateState: (partial: Partial<PlanningTabularState>) => void;
  state: Pick<PlanningTabularState, "isFetching" | "results" | "rowSelection">;
}

interface UseTableHighlightNavigationReturn {
  toastHighlightSelectionRef: MutableRefObject<RowSelectionState | null>;
  toastHighlightExpansionRef: MutableRefObject<ExpandedState | null>;
  pendingToastScrollRef: MutableRefObject<string | null>;
  pendingFindPageByEntityRef: MutableRefObject<string | null>;
}

export function useTableHighlightNavigation(
  params: UseTableHighlightNavigationParams
): UseTableHighlightNavigationReturn {
  const { tableRef, latestStateRef, searchCtrlRef, getDataCache, updateState, state } = params;

  const toastHighlightSelectionRef = useRef<RowSelectionState | null>(null);
  const toastHighlightExpansionRef = useRef<ExpandedState | null>(null);
  const pendingToastScrollRef = useRef<string | null>(null);
  const pendingFindPageByEntityRef = useRef<string | null>(null);

  const { scrollToSelectedRow } = useScrollToRow();

  const navigateToTableRow = useCallback(
    (
      positionId: string,
      scrollTargetId: string,
      newSelection: RowSelectionState,
      expansion?: ExpandedState
    ) => {
      const hasActiveSearch = !!searchCtrlRef.current?.getQueryString();
      const hasActiveFilters = latestStateRef.current!.columnFilters.length > 0;
      const hasActiveSort = latestStateRef.current!.sorting.length > 0;
      searchCtrlRef.current?.resetQuery();

      const { pageSize, pageIndex } = latestStateRef.current!.pagination;
      const cachedGlobalIndex =
        !hasActiveSearch && !hasActiveFilters && !hasActiveSort
          ? getDataCache()[positionId]?._globalIndex
          : undefined;
      const expansionState = { expanded: expansion ?? {} };

      if (cachedGlobalIndex !== undefined) {
        const targetPageIndex = Math.floor(cachedGlobalIndex / pageSize);
        if (targetPageIndex !== pageIndex) {
          pendingToastScrollRef.current = scrollTargetId;
          updateState({
            rowSelection: newSelection,
            ...expansionState,
            pagination: { pageIndex: targetPageIndex, pageSize },
            isFetching: true
          });
          return;
        }
        pendingToastScrollRef.current = scrollTargetId;
        updateState({ rowSelection: newSelection, ...expansionState });
        return;
      } else {
        pendingFindPageByEntityRef.current = positionId;
        pendingToastScrollRef.current = scrollTargetId;
        updateState({
          rowSelection: newSelection,
          ...expansionState,
          sorting: [],
          columnFilters: [],
          isFetching: true
        });
      }
    },
    [getDataCache, searchCtrlRef, updateState]
  );

  // If a second toast fired while a fetch was already in-flight, its
  // pendingFindPageByEntity target was written to the ref but the effect above
  // never re-ran (isFetching was already true). Trigger a follow-up fetch cycle
  // once the current one settles so the queued target is consumed.
  useEffect(() => {
    if (state.isFetching || !pendingFindPageByEntityRef.current) {
      return;
    }
    updateState({ isFetching: true });
  }, [state.isFetching, updateState]);

  // Scrolls to the highlighted row once the DOM is ready after a toast navigation.
  // For cross-page navigations, isFetching/results signal fetch completion. For same-page
  // navigations, calling scrollToSelectedRow immediately after updateState is unsafe because React
  // may not have committed the new selection/expansion to the DOM yet. rowSelection is included as
  // a dep so this effect fires after the same-page updateState call, deferring the scroll until
  // after React renders the new state.
  useEffect(() => {
    if (state.isFetching || !pendingToastScrollRef.current) {
      return;
    }
    // If a second findPageByEntity fetch is still queued, preserve the scroll target — it belongs
    // to that upcoming fetch, not to this just-completed one. The re-fetch effect will trigger
    // the second fetch, and the scroll will fire once that fetch settles.
    if (pendingFindPageByEntityRef.current) {
      return;
    }
    pendingToastScrollRef.current = null;
    scrollToSelectedRow(tableRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isFetching, state.results, state.rowSelection]);

  useTopic("highlightPlanningTableRow", ({ positionId }: { positionId: string }) => {
    if (!positionId) return;
    toastHighlightExpansionRef.current = null; // clear stale expansion from prior subrow nav

    const newSelection: RowSelectionState = { [positionId]: true };
    const cachedPosition = getDataCache()[positionId];
    if (cachedPosition?.subRowsMap) {
      Object.keys(cachedPosition.subRowsMap).forEach((subRowId: string) => {
        newSelection[subRowId] = true;
      });
    }

    toastHighlightSelectionRef.current = newSelection;
    navigateToTableRow(positionId, positionId, newSelection);
  });

  useTopic(
    "highlightPlanningTableSubrow",
    ({ employeeId, positionId }: { employeeId: string; positionId: string }) => {
      if (!employeeId || !positionId) return;

      // Only expand to subrow if the target position has employees OTHER than this one.
      // When the employee is the position's first/only occupant, position-row navigation is correct.
      // If the position is not yet in the cache (off-page), we cannot determine occupancy —
      // default to subrow expansion since this handler explicitly targets an employee subrow.
      const cachedPosition = getDataCache()[positionId];
      const hasOtherEmployees =
        !cachedPosition ||
        Object.keys(cachedPosition.subRowsMap ?? {}).some(id => id !== employeeId);

      if (hasOtherEmployees) {
        const newSelection: RowSelectionState = { [positionId]: true, [employeeId]: true };
        const newExpansion: ExpandedState = { [positionId]: true };
        toastHighlightSelectionRef.current = newSelection;
        toastHighlightExpansionRef.current = newExpansion;
        navigateToTableRow(positionId, employeeId, newSelection, newExpansion);
      } else {
        // Same logic as highlightPlanningTableRow: select the position row only
        toastHighlightExpansionRef.current = null;
        const newSelection: RowSelectionState = { [positionId]: true, [employeeId]: true };
        toastHighlightSelectionRef.current = newSelection;
        navigateToTableRow(positionId, positionId, newSelection);
      }
    }
  );

  return {
    toastHighlightSelectionRef,
    toastHighlightExpansionRef,
    pendingToastScrollRef,
    pendingFindPageByEntityRef
  };
}
